import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { collection, doc, limit, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../../lib/firebase";

function formatDate(ts) {
  if (!ts) return "";
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString();
}

function getMillis(ts) {
  if (!ts) return 0;
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.getTime();
}

function truncate(str, n = 140) {
  if (!str) return "";
  const s = String(str).trim();
  return s.length > n ? s.slice(0, n) + "..." : s;
}

function regionLabel(country) {
  const c = (country || "").toLowerCase().trim();
  if (c === "canada" || c.includes("canada")) return "Province";
  if (c === "ireland" || c.includes("ireland")) return "County";
  if (
    c === "united states" ||
    c === "united states of america" ||
    c === "usa" ||
    c === "us" ||
    c.includes("united states")
  )
    return "State";
  return "Region";
}

export default function ClientDetail() {
  const { id } = useParams();

  const [client, setClient] = useState(null);
  const [loadingClient, setLoadingClient] = useState(true);

  const [docsList, setDocsList] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);

  useEffect(() => {
    if (!id) return;

    const ref = doc(db, "clients", id);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const next = snap.exists() ? { id: snap.id, ...snap.data() } : null;
        setClient(next);
        setLoadingClient(false);
      },
      () => {
        setClient(null);
        setLoadingClient(false);
      }
    );

    return () => unsub();
  }, [id]);

  useEffect(() => {
    if (!id) return;

    const q = query(collection(db, "evaluationRequests"), where("clientId", "==", id), limit(500));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => getMillis(b.createdAt) - getMillis(a.createdAt));
        setDocsList(rows);
        setLoadingDocs(false);
      },
      () => {
        setDocsList([]);
        setLoadingDocs(false);
      }
    );

    return () => unsub();
  }, [id]);

  const headerLine = useMemo(() => {
    if (!client) return "";
    const parts = [client.email, client.phone].filter(Boolean);
    return parts.join(" | ");
  }, [client]);

  if (loadingClient) return <p className="muted">Loading...</p>;

  if (!client) {
    return (
      <div>
        <p className="muted">Client not found.</p>
        <Link className="btn" to="/admin/clients">
          Back to Clients
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "flex-start",
        }}
      >
        <div>
          <h2 style={{ marginBottom: 6 }}>{client.name || "Client"}</h2>
          <div className="micro muted">{headerLine}</div>
        </div>

        <div style={{ textAlign: "right", minWidth: 220 }}>
          <div className="micro muted">Created: {formatDate(client.createdAt)}</div>
          <div className="micro muted" style={{ marginTop: 6 }}>
            Updated: {formatDate(client.lastUpdatedAt)}
          </div>

          <div style={{ marginTop: 10 }}>
            <Link className="btn primary" to={`/admin/clients/${client.id}/edit`}>
              Edit Client
            </Link>
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: 16,
          display: "grid",
          gap: 10,
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        }}
      >
        <div
          style={{
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 12,
            padding: 12,
            background: "rgba(255,255,255,0.7)",
          }}
        >
          <div className="micro muted">Email</div>
          <div>{client.email || "-"}</div>
        </div>
        <div
          style={{
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 12,
            padding: 12,
            background: "rgba(255,255,255,0.7)",
          }}
        >
          <div className="micro muted">Phone</div>
          <div>{client.phone || "-"}</div>
        </div>
        <div
          style={{
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 12,
            padding: 12,
            background: "rgba(255,255,255,0.7)",
          }}
        >
          <div className="micro muted">Organization</div>
          <div>{client.organization || "-"}</div>
        </div>
        <div
          style={{
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 12,
            padding: 12,
            background: "rgba(255,255,255,0.7)",
          }}
        >
          <div className="micro muted">Address line 1</div>
          <div>{client.addressLine1 || "-"}</div>
        </div>
        <div
          style={{
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 12,
            padding: 12,
            background: "rgba(255,255,255,0.7)",
          }}
        >
          <div className="micro muted">Address line 2</div>
          <div>{client.addressLine2 || "-"}</div>
        </div>
        <div
          style={{
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 12,
            padding: 12,
            background: "rgba(255,255,255,0.7)",
          }}
        >
          <div className="micro muted">{regionLabel(client.country)}</div>
          <div>{client.region || "-"}</div>
        </div>
        <div
          style={{
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 12,
            padding: 12,
            background: "rgba(255,255,255,0.7)",
          }}
        >
          <div className="micro muted">City</div>
          <div>{client.city || "-"}</div>
        </div>
        <div
          style={{
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 12,
            padding: 12,
            background: "rgba(255,255,255,0.7)",
          }}
        >
          <div className="micro muted">Postal code</div>
          <div>{client.postalCode || "-"}</div>
        </div>
        <div
          style={{
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 12,
            padding: 12,
            background: "rgba(255,255,255,0.7)",
          }}
        >
          <div className="micro muted">Country</div>
          <div>{client.country || "-"}</div>
        </div>
        <div
          style={{
            gridColumn: "1 / -1",
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 12,
            padding: 12,
            background: "rgba(255,255,255,0.7)",
          }}
        >
          <div className="micro muted">Notes</div>
          <div>{client.notes || "-"}</div>
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <h3 style={{ marginBottom: 8 }}>Estimates and invoices</h3>

        {loadingDocs && <p className="muted">Loading documents...</p>}

        {!loadingDocs && docsList.length === 0 && <p className="muted">No linked documents yet.</p>}

        {!loadingDocs && docsList.length > 0 && (
          <div style={{ display: "grid", gap: 12 }}>
            {docsList.map((r) => {
              const status = r.status || "new";
              const photos = Array.isArray(r.attachments) ? r.attachments.length : 0;

              return (
                <article
                  key={r.id}
                  style={{
                    border: "1px solid rgba(0,0,0,0.1)",
                    borderRadius: 14,
                    padding: 14,
                    background: "rgba(255,255,255,0.7)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ minWidth: 260 }}>
                      <strong style={{ display: "block" }}>{r.name || client.name || "Request"}</strong>
                      <div className="micro muted" style={{ marginTop: 4 }}>
                        Created: {formatDate(r.createdAt)} | Status: {status} | Photos: {photos}
                      </div>

                      {(r.lastNoteText || r.message) && (
                        <div className="micro muted" style={{ marginTop: 8 }}>
                          {r.lastNoteText ? `Last note: ${truncate(r.lastNoteText, 140)}` : truncate(r.message, 140)}
                        </div>
                      )}
                    </div>

                    <Link className="btn primary" to={`/admin/requests/${r.id}`}>
                      Open
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ marginTop: 18, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <Link className="btn" to="/admin">
          Back to Dashboard
        </Link>
        <Link className="btn" to="/admin/clients">
          Back to Clients
        </Link>
      </div>
    </div>
  );
}
